import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { consumePostLoginPath } from "@/lib/auth-navigation";
import {
  consumeKakaoOAuthState,
  loginWithCustomToken,
} from "@/lib/firebase";
import { KakaoAuthTokenSchema } from "@shared/api-schemas";
import { postJson } from "@shared/http";
import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";

export default function KakaoCallback() {
  const [, setLocation] = useLocation();
  const { user, userProfile, loading: authLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("카카오 로그인을 확인하고 있어요...");
  const exchangeStartedRef = useRef(false);

  useEffect(() => {
    if (exchangeStartedRef.current) return;
    exchangeStartedRef.current = true;

    const handleKakaoCallback = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const oauthError = params.get("error");
        const errorDescription = params.get("error_description");
        const state = params.get("state");

        if (oauthError) {
          throw new Error(errorDescription || oauthError);
        }
        if (!code) {
          throw new Error("카카오 인증 코드가 없습니다. 다시 로그인해주세요.");
        }
        if (!consumeKakaoOAuthState(state)) {
          throw new Error(
            "로그인 요청을 확인할 수 없습니다. 보안을 위해 다시 로그인해주세요.",
          );
        }

        setStatus("카카오 계정을 연결하고 있어요...");
        const redirectUri = `${window.location.origin}/api/auth/kakao/callback`;
        const data = (await postJson(
          "/api/auth/kakao/token",
          { code, redirectUri },
          KakaoAuthTokenSchema,
        )) as { customToken?: string };

        if (!data.customToken) {
          throw new Error("로그인 토큰을 받지 못했습니다.");
        }

        setStatus("OZK 계정을 준비하고 있어요...");
        await loginWithCustomToken(data.customToken);

        // URL에 일회용 인증 코드를 남기지 않는다.
        window.history.replaceState({}, "", "/auth/kakao/callback");
      } catch (callbackError: any) {
        console.error("[KakaoCallback] Login failed:", callbackError);
        setError(callbackError?.message || "로그인에 실패했습니다.");
      }
    };

    void handleKakaoCallback();
  }, []);

  useEffect(() => {
    if (error || authLoading || !user || !userProfile) return;

    setLocation(
      userProfile.nicknameRequired
        ? "/nickname/setup"
        : consumePostLoginPath(),
    );
  }, [authLoading, error, setLocation, user, userProfile]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-destructive">로그인 실패</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <button
              onClick={() => setLocation("/login")}
              className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              data-testid="button-retry-login"
            >
              로그인 다시 시도
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            카카오 로그인
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{status}</p>
        </CardContent>
      </Card>
    </div>
  );
}
